import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { useDispatch, useSelector } from "react-redux";
import { styled } from "@mui/material/styles";
import Chip from "@mui/material/Chip";
import MenuOutlinedIcon from "@mui/icons-material/MenuOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { useSnackbar } from "notistack";
import { addMarketWatchInstrumentAction } from "../../../store/actions/watchlist";
import Box from "@mui/material/Box";
import { openOrderWindow } from "../../../store/actions/orders";
import { useNavigate } from "react-router-dom";

const HoverContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: theme.spacing(0.5),
  padding: theme.spacing(0.25),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)',
  borderRadius: theme.shape.borderRadius,
  position: 'absolute',
  top: 0,
  left: 0,
  right: 'auto',
  bottom: 0,
  width: 220,
  zIndex: 10,
  [theme.breakpoints.down('md')]: {
    display: 'none' // Hide on mobile and tablet
  },
  '& .MuiChip-root': {
    margin: 0
  }
}));

const StyledChip = styled(Chip)(({ theme }) => ({
  height: 24,
  fontSize: '0.7rem',
  fontWeight: 600,
  minWidth: 50,
  '&.order-accepted': {
    backgroundColor: theme.palette.success.main,
    color: theme.palette.success.contrastText,
  },
  '&.order-rejected': {
    backgroundColor: theme.palette.error.main,
    color: theme.palette.error.contrastText,
  },
  '&.order-chart': {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
  },
  '&.order-disabled': {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[500],
    cursor: 'not-allowed',
    pointerEvents: 'none'
  },
  '&:hover': {
    opacity: 0.8
  }
}));

const defaultBuySellSettings = {
  product_type: "MIS",
  order_type: "LIMIT",
  execution_type: "REGULAR",
  validity: "DAY"
};

function CallHoverComponent({ 
  row, 
  isHover
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { watchlists, watchListIndex } = useSelector((state) => state.marketwatch);
  const configs = useSelector((state) => state.configs) || {};
  const buysellModalSetting = configs.buysellModalSetting || defaultBuySellSettings;


  const getProductType = () => {
    if (buysellModalSetting.product_type === "CNC/NRML") {
      return (row?.exchange === "NSE" || row?.exchange === "BSE") ? "CNC" : "NRML";
    }
    return buysellModalSetting.product_type || "MIS";
  };

  const addInstrument = async (e) => {
    if (watchlists[watchListIndex].instruments.length === 20) {
      enqueueSnackbar("Switch to other marketwatch to add new instrument", {
        variant: "info",
      });
      return;
    }
    let payload = {
      mw_id: watchlists[watchListIndex].id,
      exchange: row?.exchange,
      trading_symbol: row?.trading_symbol,
      token: `${row?.token}`,
      execution: row?.execution === 'SPREAD' ? 'SPREAD' : 'NORMAL'
    };

    const { message, status } = await dispatch(
      addMarketWatchInstrumentAction(watchListIndex, payload)
    );
    if (status === "success") {
      enqueueSnackbar(message, { variant: "success" });
    } else {
      enqueueSnackbar(message, { variant: "error" });
    }
  };

  const handleBuy = async (e) => {
    e.stopPropagation();
    console.log('Buy clicked with row data:', row);
    if (!row?.trading_symbol || !row?.exchange || !row?.token) {
      enqueueSnackbar("Invalid instrument data - missing trading symbol, exchange or token", { variant: "error" });
      return;
    }
    dispatch(openOrderWindow({
      type: 'BUY',
      trading_symbol: row.trading_symbol,
      display_name: row.display_name || row.trading_symbol,
      ltp: row.ltp || 0,
      exchange: row.exchange,
      token: row.token,
      product: getProductType(),
      order_type: "MARKET"
    }));
  };

  const handleSell = async (e) => {
    e.stopPropagation();
    console.log('Sell clicked with row data:', row);
    if (!row?.trading_symbol || !row?.exchange || !row?.token) {
      enqueueSnackbar("Invalid instrument data - missing trading symbol, exchange or token", { variant: "error" });
      return;
    }
    dispatch(openOrderWindow({
      type: 'SELL',
      trading_symbol: row.trading_symbol,
      display_name: row.display_name || row.trading_symbol,
      ltp: row.ltp || 0,
      exchange: row.exchange,
      token: row.token,
      product: getProductType(),
      order_type: "MARKET"
    }));
  };

  const handleChart = (e) => {
    e.stopPropagation();
    console.log('Chart clicked with row data:', row);
    if (!row?.exchange || !row?.token) {
      enqueueSnackbar("Invalid instrument data - missing exchange or token for chart", { variant: "error" });
      return;
    }
    // Navigate to overview route with chart tab
    navigate(`/overview/tdv/${row.exchange}/${row.token}`, {
      state: { defaultTab: 'chart' }
    });
  };


  return (
    <>
      {isHover && (
        <HoverContainer>
          <StyledChip 
            label="SELL"  
            disabled={!row?.tradable}
            className={!row?.tradable ? "order-disabled" : "order-rejected"}
            onClick={handleSell}
            size="small"
          />
          <StyledChip 
            label={<TrendingUpIcon sx={{ fontSize: '0.9rem' }} />}  
            className="order-chart"
            onClick={handleChart}
            size="small"
          />
          <StyledChip 
            label="BUY"  
            disabled={!row?.tradable}
            className={!row?.tradable ? "order-disabled" : "order-accepted"}
            onClick={handleBuy}
            size="small"
          />
        </HoverContainer>
      )}
    </>
  );
}

export default CallHoverComponent; 